import { html, PropertyValues, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { BaseElement, customElement } from '../../../components/base-element';
import styles from './graph-node.scss?inline';

@customElement('ps-graph-node')
export class GraphNodeWC extends BaseElement {
  static styles = unsafeCSS(styles);

  @property() variant: 'default' | 'flush' = 'default';

  @property() status: 'valid' | 'warning' | 'error' = 'valid';

  @property() label = '';

  @property() caption = '';

  @property({ type: String }) nodeId = `42`;

  @property({ type: Boolean }) disabled = false;

  /**
   * `disabled` used to be paint only — `c-graph-node--disabled` changes the
   * cursor and drops the hover, and nothing said so programmatically. A
   * consumer's only way to convey it was to drop the node's `role`, which
   * removes the entry from its menu entirely: a screen-reader user does not
   * hear "unavailable", the entry simply is not there, and the reason it is
   * unavailable goes with it.
   *
   * Reflected onto the HOST, because that is where the consumer puts
   * `role="menuitem"` — the shadow content is not the exposed node here. Set
   * as an attribute rather than a property so it lands on the same element the
   * role does (ARIA disabled is a state, not a boolean attribute: it is
   * removed, never `aria-disabled="false"`, so an enabled node reads normally).
   */
  updated(changed: PropertyValues) {
    super.updated(changed);

    if (changed.has('disabled')) {
      if (this.disabled) {
        this.setAttribute('aria-disabled', 'true');
      } else {
        this.removeAttribute('aria-disabled');
      }
    }
  }

  render() {
    const classes = classMap({
      'c-graph-node': true,
      [`c-graph-node--variant-${this.variant}`]: this.variant,
      [`c-graph-node--error`]: this.status === 'error',
      [`c-graph-node--warning`]: this.status === 'warning',
      'c-graph-node--disabled': this.disabled,
    });

    const statusClasses = classMap({
      'c-graph-node__status': true,
      'c-graph-node__status-pill': true,
    });

    const isDefault = this.variant === 'default';

    const label = isDefault
      ? html` <ps-text-subtitle truncate>${this.label}</ps-text-subtitle> `
      : html` <ps-text-body truncate> ${this.label} </ps-text-body> `;

    const caption = isDefault
      ? html` <ps-text-body truncate>${this.caption}</ps-text-body> `
      : html`
          <ps-text-caption class="c-graph-node__caption"
            >${this.caption}</ps-text-caption
          >
        `;

    return html`
      <div class=${classes}>
        <div class=${statusClasses}></div>

        <div class="c-graph-node__header">
          <div class="c-graph-node__label">${label}</div>
          <div class="c-graph-node__id">
            <ps-text-caption variant="tertiary">${this.nodeId}</ps-text-caption>
          </div>
        </div>

        ${caption}

        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-graph-node': GraphNodeWC;
  }
}
